import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { FiUser, FiLock, FiSave, FiEye, FiEyeOff } from 'react-icons/fi';
import DashboardShell from '../../components/DashboardShell/DashboardShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import WalletBadge from '../../components/WalletBadge/WalletBadge';
import styles from './AccountSettings.module.css';
import axiosClient from '../../api/axiosClient';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { setUser } from '../../features/auth/authSlice';

const AccountSettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  const [fullName, setFullName] = useState(user?.full_name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [fundOrg, setFundOrg] = useState((user as any)?.fund_org || '');
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const { data } = await axiosClient.patch('/auth/profile', { fullName, phone, fundOrg });
      dispatch(setUser(data.user));
      toast.success('Profile updated.');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not update profile.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error('New passwords do not match.'); return; }
    setSavingPassword(true);
    try {
      await axiosClient.patch('/auth/change-password', { currentPassword, newPassword });
      toast.success('Password updated.');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not update password.');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <DashboardShell>
      <PageHeader title="Account Settings" subtitle="Update your profile details and manage your password." action={<WalletBadge />} />

      <div className={styles.page}>
        <div className={styles.profileHero}>
          <div className={styles.profileHeroAvatar}>{user?.full_name?.[0]?.toUpperCase() || '?'}</div>
          <div>
            <div className={styles.profileHeroName}>{user?.full_name || 'Your account'}</div>
            <div className={styles.profileHeroEmail}>{user?.email}</div>
          </div>
          <div className={styles.profileHeroRole}>{user?.role}</div>
        </div>

        <form className={styles.card} onSubmit={handleProfileSave}>
          <div className={styles.cardHeader}><div className={`${styles.cardIcon} ${styles.cardIconBlue}`}><FiUser /></div><h3>Profile</h3></div>

          <label className={styles.label}>Full Name</label>
          <input className={styles.input} value={fullName} onChange={(e) => setFullName(e.target.value)} required />

          <label className={styles.label}>Email</label>
          <input className={styles.input} value={user?.email || ''} disabled style={{ opacity: 0.6, cursor: 'not-allowed' }} />

          <label className={styles.label}>Phone</label>
          <input className={styles.input} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone number" />

          <label className={styles.label}>Fund / Organisation</label>
          <input className={styles.input} value={fundOrg} onChange={(e) => setFundOrg(e.target.value)} placeholder="Optional" />

          <button className={styles.saveBtn} type="submit" disabled={savingProfile}>
            <FiSave /> {savingProfile ? 'Saving…' : 'Save Profile'}
          </button>
        </form>

        <form className={styles.card} onSubmit={handlePasswordSave}>
          <div className={styles.cardHeader}><div className={`${styles.cardIcon} ${styles.cardIconOrange}`}><FiLock /></div><h3>Change Password</h3></div>

          <label className={styles.label}>Current Password</label>
          <div className={styles.pwWrap}>
            <input className={styles.input} type={showCurrent ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
            <button type="button" className={styles.eyeToggle} onClick={() => setShowCurrent((v) => !v)} tabIndex={-1}>
              {showCurrent ? <FiEyeOff /> : <FiEye />}
            </button>
          </div>

          <label className={styles.label}>New Password</label>
          <div className={styles.pwWrap}>
            <input className={styles.input} type={showNew ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6} />
            <button type="button" className={styles.eyeToggle} onClick={() => setShowNew((v) => !v)} tabIndex={-1}>
              {showNew ? <FiEyeOff /> : <FiEye />}
            </button>
          </div>

          <label className={styles.label}>Confirm New Password</label>
          <div className={styles.pwWrap}>
            <input className={styles.input} type={showConfirm ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} />
            <button type="button" className={styles.eyeToggle} onClick={() => setShowConfirm((v) => !v)} tabIndex={-1}>
              {showConfirm ? <FiEyeOff /> : <FiEye />}
            </button>
          </div>

          <button className={styles.saveBtn} type="submit" disabled={savingPassword}>
            <FiSave /> {savingPassword ? 'Updating…' : 'Update Password'}
          </button>
        </form>
      </div>
    </DashboardShell>
  );
};

export default AccountSettings;
